import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { requestOrderSummary, getOrderSummaryStatus } from "../services/orderSummaryApi";

/**
 * AISummaryModal — centered popup modal for an order's AI-generated summary.
 * Rendered via portal into document.body with a dimmed backdrop.
 * Closes on backdrop click, Esc, or the ✕ button.
 *
 * Props:
 *   isOpen           boolean
 *   onClose          () => void
 *   orderId          string   (stable cache key, e.g. "CaseNo_OrderDate")
 *   orderLink        string?  (direct URL to order PDF)
 *   pdfProxyUrl      string?  (backend proxy URL)
 *   pdfStorageUrl    string?  (Firebase Storage URL)
 */
function AISummaryModal({ isOpen, onClose, orderId, orderLink, pdfProxyUrl, pdfStorageUrl }) {
  const [status, setStatus] = useState("idle"); // idle | loading | completed | failed
  const [summary, setSummary] = useState(null);
  const [summaries, setSummaries] = useState(null);
  const [failureReason, setFailureReason] = useState(null);
  const [jobId, setJobId] = useState(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const panelRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Start polling when we have a jobId that isn't completed/failed
  const startPolling = useCallback((jid) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const res = await getOrderSummaryStatus(jid);
        if (!mountedRef.current) return;

        if (res.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSummary(res.summary);
          if (res.summaries) setSummaries(res.summaries);
          setStatus("completed");
        } else if (res.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setFailureReason(res.failureReason || "unknown");
          setStatus("failed");
        }
        // else still pending/processing — keep polling
      } catch (err) {
        console.error("Polling error:", err);
        // Don't stop polling on transient network errors
      }
    }, 2000);
  }, []);

  // Kick off the summary request when panel opens
  useEffect(() => {
    if (!isOpen || !orderId) return;

    // Reset state for fresh open
    setStatus("loading");
    setSummary(null);
    setSummaries(null);
    setFailureReason(null);
    setJobId(null);

    let cancelled = false;

    (async () => {
      try {
        const res = await requestOrderSummary({
          orderId,
          orderLink: orderLink || undefined,
          pdfProxyUrl: pdfProxyUrl || undefined,
          pdfStorageUrl: pdfStorageUrl || undefined,
        });

        if (cancelled || !mountedRef.current) return;

        setJobId(res.jobId);

        if (res.status === "completed" && res.summary) {
          // Cache hit — instant result
          setSummary(res.summary);
          if (res.summaries) setSummaries(res.summaries);
          setStatus("completed");
        } else {
          // Need to poll
          startPolling(res.jobId);
        }
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        console.error("Order summary request error:", err);
        setFailureReason("llm_failed");
        setStatus("failed");
      }
    })();

    return () => {
      cancelled = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isOpen, orderId, orderLink, pdfProxyUrl, pdfStorageUrl, startPolling]);

  // Handle retry
  const handleRetry = () => {
    setStatus("loading");
    setSummary(null);
    setSummaries(null);
    setFailureReason(null);
    setJobId(null);

    (async () => {
      try {
        const res = await requestOrderSummary({
          orderId,
          orderLink: orderLink || undefined,
          pdfProxyUrl: pdfProxyUrl || undefined,
          pdfStorageUrl: pdfStorageUrl || undefined,
        });

        if (!mountedRef.current) return;

        setJobId(res.jobId);

        if (res.status === "completed" && res.summary) {
          setSummary(res.summary);
          if (res.summaries) setSummaries(res.summaries);
          setStatus("completed");
        } else if (res.status === "failed") {
          setFailureReason(res.failureReason || "unknown");
          setStatus("failed");
        } else {
          startPolling(res.jobId);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setFailureReason("llm_failed");
        setStatus("failed");
      }
    })();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      style={{ animation: "aiModalFadeIn 0.2s ease-out" }}
      onClick={onClose}
    >
      <style>{`
        @keyframes aiModalFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes aiModalPopIn {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      <div
        ref={panelRef}
        className="bg-white rounded-2xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden w-full max-w-lg"
        style={{ maxHeight: "80vh", animation: "aiModalPopIn 0.25s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-purple-500" />
            <h2 className="text-gray-900 font-semibold text-base">AI Order Summary</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 overflow-y-auto">
          {/* Success or Partial Success */}
          {(status === "completed" || (status === "processing" && summaries && summaries.length > 0)) && (
            <div className="flex flex-col gap-3 pb-4">
              {summaries && summaries.length > 1 && (
                <p className="text-sm text-gray-500">
                  {summaries.length} orders detected in this document
                  {status === "processing" && " (processing...)"}
                </p>
              )}
              {summaries && summaries.length > 0 ? (
                [...summaries]
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((s) => {
                    // Hide the placeholder case number the backend emits when it
                    // couldn't detect one; label by position only in multi-order docs.
                    const noCaseNo = s.case_no === "UNKNOWN" ||
                      (s.case_no.startsWith("ORDER_") && s.case_no.includes("_UNRESOLVED"));
                    const heading = noCaseNo
                      ? (summaries.length > 1 ? `Order ${s.order_index + 1}` : null)
                      : s.case_no;
                    return (
                  <div
                    key={s.order_index}
                    className={`rounded-xl p-4 border ${s.is_error ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    {(heading || s.is_error) && (
                      <div className="flex items-center gap-2 mb-2">
                        {s.is_error && <span className="text-red-500 text-sm">⚠️</span>}
                        {heading && (
                          <h4 className={`${s.is_error ? 'text-red-600' : 'text-gray-900'} font-semibold text-sm`}>
                            {heading}
                          </h4>
                        )}
                      </div>
                    )}
                    <p className={`${s.is_error ? 'text-red-600/90' : 'text-gray-700'} text-sm leading-relaxed whitespace-pre-line`}>
                      {s.summary}
                    </p>
                    {s.is_error && (
                      <button
                        onClick={handleRetry}
                        className="mt-3 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Retry
                      </button>
                    )}
                  </div>
                    );
                  })
              ) : summary ? (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                    {summary.replace(/^\*\*(?:UNKNOWN|ORDER_\d+_UNRESOLVED)\*\*\s*\n?/gm, "").trim()}
                  </p>
                </div>
              ) : null}
            </div>
          )}

          {/* Loading state without any partial results yet */}
          {status === "loading" || (status === "processing" && (!summaries || summaries.length === 0)) ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <Loader2 className="w-7 h-7 text-purple-600 animate-spin" />
              <p className="text-gray-600 text-sm">Generating AI Summary...</p>
              <p className="text-gray-400 text-xs">This may take a few seconds</p>
            </div>
          ) : null}

          {/* Processing spinner appended if we have partial results */}
          {status === "processing" && summaries && summaries.length > 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-2 border-t border-gray-200 mt-2">
              <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
              <p className="text-gray-500 text-xs">Processing remaining orders...</p>
            </div>
          )}

          {/* Error: PDF unavailable */}
          {status === "failed" && failureReason === "pdf_unavailable" && (
            <div className="flex flex-col items-center justify-center gap-3 py-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <span className="text-xl">📄</span>
              </div>
              <p className="text-gray-600 text-sm text-center">
                PDF not available. The court server may be temporarily unavailable.
              </p>
              <button
                onClick={handleRetry}
                className="mt-1 px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Error: Extraction failed */}
          {status === "failed" && failureReason === "extraction_failed" && (
            <div className="flex flex-col items-center justify-center gap-3 py-4">
              <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center">
                <span className="text-xl">🔍</span>
              </div>
              <p className="text-gray-600 text-sm text-center">
                Could not extract text from this order PDF. It may be a scanned image document.
              </p>
              <button
                onClick={handleRetry}
                className="mt-1 px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Error: LLM / generic failure */}
          {status === "failed" && failureReason !== "pdf_unavailable" && failureReason !== "extraction_failed" && (
            <div className="flex flex-col items-center justify-center gap-3 py-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <span className="text-xl">⚠️</span>
              </div>
              <p className="text-gray-600 text-sm text-center">
                Summary generation failed. Please try again later.
              </p>
              <button
                onClick={handleRetry}
                className="mt-1 px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AISummaryModal;
